import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { Role, ViewKey } from '@/domain';
import { strings } from '@/shared/strings';
import { BellIcon, MenuIcon, OfflineIcon, SearchIcon, SyncIcon } from '../icons';
import { BottomTabs } from './BottomTabs';
import { Sidebar } from './Sidebar';
import { useScrimVisible, useShellStore } from './shellStore';
import { HEADER_SLOT_ID } from './headerSlot';
import styles from './AppShell.module.css';

/**
 * The width at which the menu stops being a drawer, from
 * `Sidebar.module.css`. Stated here because a scrim over a menu that is part of
 * the page is the bug this closes, and CSS cannot tell the store about it.
 */
const DESKTOP_QUERY = '(min-width: 1081px)';

export interface AppShellProps {
  title: string;
  role: Role;
  views: readonly ViewKey[];
  userName: string;
  userTitle: string;
  canScan: boolean;
  unreadCount: number;
  /** Section 9. The quiet strip, not a modal and not a red banner. */
  offline: boolean;
  /** Commands recorded but not yet accepted by the server. */
  notSyncedCount: number;
  /** True when at least one of them was refused rather than merely delayed. */
  notSyncedFailed: boolean;
  onOpenNotSynced: () => void;
  onOpenNotifications: () => void;
  /** The magnifying glass. Finds a case by job order, kit or QR code. */
  onOpenSearch: () => void;
  /** The key on the user block: change your own password. */
  onChangePassword: () => void;
  onSignOut: () => void;
  /** The person's photograph as an object URL, or null for their initials. */
  avatarSrc?: string | null;
  /** The loaded version and its release day, as the profile and the sign in screen show it. */
  version?: string;
  onOpenProfile?: () => void;
  /**
   * A strip between the top bar and the content, above the scroll rather than in
   * it. The running tasks live here, section 2.4 of the request of 20 September
   * 2026: a technician on any screen has to be able to see what is still counting
   * and stop it, so it cannot belong to one screen.
   *
   * A node rather than the tasks themselves, because the clocks come from the
   * application layer and this one may not reach it.
   */
  banner?: ReactNode;
  children: ReactNode;
}

export function AppShell({
  title,
  role,
  views,
  userName,
  userTitle,
  canScan,
  unreadCount,
  offline,
  notSyncedCount,
  notSyncedFailed,
  onOpenNotSynced,
  onOpenNotifications,
  onOpenSearch,
  onChangePassword,
  onSignOut,
  avatarSrc = null,
  version,
  onOpenProfile,
  banner,
  children,
}: AppShellProps) {
  const openNav = useShellStore((state) => state.openNav);
  const closeNav = useShellStore((state) => state.closeNav);
  const closeAll = useShellStore((state) => state.closeAll);
  const scrimVisible = useScrimVisible();
  const { pathname } = useLocation();

  /*
   * The menu closes when the screen changes, wherever the navigation came from.
   *
   * Every entry in the menu closes it on the way out, and that was enough while
   * the menu was the only thing that navigated. It is not: a toast action, the
   * scanner and a redirect all move the screen underneath an open menu and
   * leave the scrim over it.
   */
  useEffect(() => {
    closeNav();
  }, [closeNav, pathname]);

  /*
   * And when the window grows past the width where the menu stops being a
   * drawer. Above it the menu is a column of the page, so there is nothing for
   * a scrim to sit over, and leaving one there covers a screen nobody thinks
   * is covered.
   */
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const update = () => {
      if (query.matches) closeNav();
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [closeNav]);

  // The two roles that work a bench sign in at a station inside the lab radius.
  // Everyone else works from a desk, so the pill is noise for them.
  const atStation = role === 'tech' || role === 'cadcam';

  return (
    <>
      <a className={styles.skip} href="#main">
        {strings.shell.skipToContent}
      </a>

      <div className={styles.app}>
        <Sidebar
          role={role}
          views={views}
          canScan={canScan}
          userName={userName}
          userTitle={userTitle}
          onChangePassword={onChangePassword}
          onSignOut={onSignOut}
          avatarSrc={avatarSrc}
          version={version}
          onOpenProfile={onOpenProfile}
        />

        <main className={styles.main}>
          <div className={styles.tbar}>
            <button
              className={styles.hamb}
              onClick={openNav}
              aria-label={strings.shell.openMenu}
              aria-controls="sidebar"
            >
              <MenuIcon />
            </button>

            <h1>{title}</h1>

            <div className={styles.right}>
              {/* A screen's own header action on a phone, such as its refresh button. */}
              <div id={HEADER_SLOT_ID} className={styles.slot} />
              {/* The "In lab radius" pill went with the request of 4 October
                  2026, section 1: it showed whether or not anyone was in the lab. */}
              {!atStation && (
                <button
                  className={styles.iconbtn}
                  aria-label={strings.shell.search}
                  onClick={onOpenSearch}
                >
                  <SearchIcon />
                </button>
              )}

              {notSyncedCount > 0 && (
                <button
                  className={`${styles.sync} ${notSyncedFailed ? styles.syncBad : ''}`}
                  onClick={onOpenNotSynced}
                  aria-label={`${strings.sync.trayLabel}: ${notSyncedCount}`}
                >
                  <SyncIcon />
                  {notSyncedCount}
                </button>
              )}

              {/*
                The count goes IN the label, and the badge is hidden from the
                reading order.

                A button labelled "Notifications" with a visible "3" inside it
                is a label that does not contain its own visible text, which
                axe flags and a voice control user feels: they say "click
                notifications 3" and nothing happens. One label, both facts.
              */}
              <button
                className={styles.iconbtn}
                aria-label={
                  unreadCount > 0
                    ? `${strings.shell.notifications}: ${String(unreadCount)} ${strings.shell.unread}`
                    : strings.shell.notifications
                }
                onClick={onOpenNotifications}
              >
                <BellIcon />
                {unreadCount > 0 && (
                  <span className={styles.badge} aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/*
            Section 9. Quiet and persistent, above the content rather than over
            it. Red is reserved for urgent, and being offline is normal in this
            building.
          */}
          {offline && (
            <p className={styles.offline} role="status">
              <OfflineIcon />
              {strings.sync.offline}
            </p>
          )}

          {/* The running tasks, on every screen. Section 2.4. */}
          {banner}

          <div className={styles.body} id="main">
            <div className={styles.wrap}>{children}</div>
          </div>
        </main>
      </div>

      <BottomTabs role={role} views={views} canScan={canScan} />

      {/*
        The scrim is derived from whichever panel is open rather than toggled by
        each of them, so closing one panel can never leave a dead overlay over
        the whole screen. Section 12.
      */}
      <button
        type="button"
        className={`${styles.scrim} ${scrimVisible ? styles.scrimOn : ''}`}
        onClick={closeAll}
        tabIndex={scrimVisible ? 0 : -1}
        aria-hidden={!scrimVisible}
        aria-label={strings.shell.closeOverlay}
      />
    </>
  );
}
